import dynamic from 'next/dynamic';
import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';

import { IconButton } from '@/core/tamagoshiTailwind/components/IconButton/IconButton';
import { Typography } from '@/core/tamagoshiTailwind/components/Typography/Typography';
import { CrossIcon } from '@/core/tamagoshiTailwind/icons/CrossIcon';
import { Image } from '@/domains/core/Image/Image';
import { Link } from '@/domains/core/routing/components/Link';
import { Gtm } from '@/domains/core/tracking/utils/Gtm';
import type { CampaignModalProps } from '@/domains/productDiscovery/CommercialAnimation/components/CampaignModal/CampaignModal';
import { useMarketingCampaigns } from '@/domains/productDiscovery/CommercialAnimation/hooks/useMarketingCampaigns';
import type { BannerCampaign } from '@/domains/productDiscovery/CommercialAnimation/interfaces/bannerCampaign';
import { GTM_CLICK_ON_BANNER_CAMPAIGN } from '@/productDiscovery/CommercialAnimation/tracking/events/clickOnBannerCampaign';
import { PromoItem } from '@/productDiscovery/HeaderLeonidas/b2b/components/Promos/PromoItem';
import { PartnerAdBanner } from '@/sellerAdsManagement/PartnerAdBannerCampaign/components/PartnerAdBanner';
import { spaceSlugType } from '@/sellerAdsManagement/PartnerAdBannerCampaign/interfaces/spaceSlugTypes';

import { setPromosVisibility } from '../../store/HeaderLeonidas.actions';
import translations from './translations';
import { usePromos } from './usePromos';

const DynamicModal = dynamic<CampaignModalProps>(
  async () =>
    (
      await import(
        '@/domains/productDiscovery/CommercialAnimation/components/CampaignModal/CampaignModal'
      )
    ).CampaignModal,
);

export const Promos = () => {
  const { isPromosVisible, onCloseButton, promosRef } = usePromos();
  const promos = useMarketingCampaigns();
  const dispatch = useDispatch();
  const [displayModal, setDisplayModal] = useState(false);

  const handleCampaignClick = useCallback(
    (campaign: BannerCampaign, index: number, showModal: boolean) => {
      if (showModal) {
        setDisplayModal(true);
      } else {
        dispatch(setPromosVisibility(false));
      }
      Gtm.push(
        GTM_CLICK_ON_BANNER_CAMPAIGN(
          'promo_block',
          campaign.id,
          campaign.name,
          campaign.link.url || '',
          index,
          campaign.sponsor !== null && campaign.advertiser !== null,
          campaign.pageType || undefined,
          campaign.trackingId || undefined,
        ),
      );
    },
    [dispatch],
  );

  if (!isPromosVisible || !promos?.length) return null;

  return (
    <div
      className="desktop:absolute desktop:left-0 desktop:right-0
        desktop:top-[100%] desktop:mx-auto desktop:max-w-[1380px]"
      ref={promosRef}
    >
      <div
        className="fixed bottom-0 left-0 z-[80] flex max-h-[85vh] w-full
          flex-col rounded-t-modal bg-foundation-inverted p-m px-m pb-m pt-l
          desktop:absolute desktop:bottom-[unset]
          desktop:max-h-[calc(100vh-210px)] desktop:max-w-[calc(100%-48px)]
          desktop:rounded-b-modal desktop:rounded-t-0"
      >
        <div className="flex items-center justify-between p-m desktop:hidden">
          <Typography as="h4" scale="title3">
            {translations.label}
          </Typography>
          <IconButton
            aria-label={translations.close}
            icon={CrossIcon}
            onClick={onCloseButton}
          />
        </div>
        <ul
          className="grid grow list-none
            grid-cols-[repeat(auto-fill,minmax(280px,1fr))] justify-center
            gap-y-m overflow-y-auto"
        >
          {promos.map((campaign, index) => {
            let content;
            const { id, name, image: campaignImage, link } = campaign;
            const image = (
              <Image
                src={campaignImage.mobileUrl}
                alt={name}
                height="280"
                width="280"
                className="h-[280px] w-[280px] rounded-container"
              />
            );
            if (!link.url) {
              content = (
                <div data-privatesale-id={id} data-privatesale-name={name}>
                  {image}
                </div>
              );
            } else {
              content =
                link.url && link.target !== '_modal' ? (
                  <Link
                    href={link.url}
                    data-privatesale-id={id}
                    data-privatesale-name={name}
                    onClick={() => handleCampaignClick(campaign, index, false)}
                    tabIndex={index}
                    role="link"
                    target={link.target}
                  >
                    {image}
                  </Link>
                ) : (
                  <>
                    <button
                      className="cursor-pointer bg-transparent"
                      data-privatesale-id={id}
                      data-privatesale-name={name}
                      data-testid="button-for-modal"
                      onClick={() =>
                        handleCampaignClick(
                          campaign,
                          index,
                          link.target === '_modal',
                        )
                      }
                      type="button"
                    >
                      {image}
                    </button>
                    {displayModal && (
                      <DynamicModal
                        url={link.url}
                        onClose={() => {
                          dispatch(setPromosVisibility(false));
                          setDisplayModal(false);
                        }}
                      />
                    )}
                  </>
                );
            }

            return campaign.isPartnerAd ? (
              <PartnerAdBanner
                key={id}
                partnerAdBannerCampaign={campaign}
                bannerTypeTracking="onglet_banner"
                spaceSlug={spaceSlugType.PROMO_BLOCK}
                containerClassName="relative h-[280px] w-[280px] rounded-card"
              />
            ) : (
              <PromoItem
                key={id}
                campaign={campaign}
                position={index}
                content={content}
              />
            );
          })}
        </ul>
      </div>
      <div
        className="hidden desktop:absolute desktop:right-0 desktop:top-m
          desktop:z-[80] desktop:block"
      >
        <IconButton
          aria-label={translations.close}
          icon={CrossIcon}
          onClick={onCloseButton}
        />
      </div>
    </div>
  );
};
